"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { View, StyleSheet } from "react-native"
import { Text, ActivityIndicator, useTheme, IconButton } from "react-native-paper"
import { GoogleMap, useJsApiLoader, Polyline, Marker, InfoWindow } from "@react-google-maps/api"
import type { Project } from "../../types/project"
import { useAuth } from "../../contexts/AuthContext"
import { getVoltageColor } from "../../constants/voltageColors"
// Import the environment utility
import { GOOGLE_MAPS_API_KEY, hasValidGoogleMapsKey } from "../../utils/environment"
import { Tag } from "lucide-react"
// Import map styles
import { getMapStyles } from "../../constants/mapStyles"

// Declare google variable to fix the undeclared variable issue
declare global {
  interface Window {
    google: any
  }
}

interface MapSectionProps {
  projects: Project[]
}

// Define the structure for our processed tower data
interface ProcessedTower {
  id: string
  name: string
  lat: number
  lng: number
  isStartTower: boolean
  isEndTower: boolean
  locationName?: string
  projectId: string
  projectName: string
}

// Define the structure for our processed line data
interface ProcessedLine {
  id: string
  projectId: string
  projectName: string
  fromTower: string
  toTower: string
  voltage: number
  path: { lat: number; lng: number }[]
}

export function MapSection({ projects }: MapSectionProps) {
  const theme = useTheme()
  const { apiEndpoint } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processedTowers, setProcessedTowers] = useState<ProcessedTower[]>([])
  const [processedLines, setProcessedLines] = useState<ProcessedLine[]>([])
  const [selectedMarker, setSelectedMarker] = useState<{
    position: { lat: number; lng: number }
    info: string
  } | null>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  // Add state for label visibility
  const [showLabels, setShowLabels] = useState<boolean>(true)

  // Load Google Maps API with the key from Expo Constants
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    // Only load the map if we have a valid API key
    preventGoogleFontsLoading: !hasValidGoogleMapsKey,
  })

  // Add a warning if the API key is missing
  useEffect(() => {
    if (!hasValidGoogleMapsKey) {
      console.warn("Google Maps API key is missing or invalid. Map will display in development mode.")
    }
  }, [])

  // Fetch and process project data
  useEffect(() => {
    const fetchProjectsData = async () => {
      if (!projects.length || !apiEndpoint) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const projectDataPromises = projects.map(async (project) => {
          try {
            // First, fetch the list of files for this project
            const response = await fetch(project.url)
            if (!response.ok) {
              throw new Error(`Failed to fetch project files: ${response.statusText}`)
            }

            const directoryData = await response.json()

            // Find the Info.json file in the files list
            const infoFile = directoryData.files?.find((file: any) => file.name === "Info.json")

            if (!infoFile) {
              console.warn(`Info.json not found for project ${project.name}`)
              return null
            }

            // Fetch the Info.json file content
            const infoResponse = await fetch(infoFile.url)

            if (!infoResponse.ok) {
              throw new Error(`Failed to fetch Info.json: ${infoResponse.statusText}`)
            }

            const infoData = await infoResponse.json()

            return {
              ...project,
              data: infoData,
            }
          } catch (err) {
            console.error(`Error fetching data for project ${project.name}:`, err)
            return null
          }
        })

        const projectsWithData = await Promise.all(projectDataPromises)
        const validProjects = projectsWithData.filter(Boolean) as (Project & { data: any })[]

        processProjectData(validProjects)
      } catch (err: any) {
        console.error("Error fetching projects data:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProjectsData()
  }, [projects, apiEndpoint])

  // Process project data to extract towers and lines
  const processProjectData = (projectsWithData: (Project & { data: any })[]) => {
    try {
      const allTowers: Record<
        string,
        {
          id: string
          name: string
          lat: number
          lng: number
          locationName?: string
          projectId: string
          projectName: string
        }
      > = {}
      const allLines: ProcessedLine[] = []

      // Track start and end tower names for each project
      const projectStartEndTowers: Record<
        string,
        {
          startTowerName: string
          endTowerName: string
          startLocationName: string
          endLocationName: string
        }
      > = {}

      projectsWithData.forEach((project) => {
        if (!project.data?.Lines || !project.id) return

        // First, identify start and end towers from the Line level properties
        let startTowerName = ""
        let endTowerName = ""
        let startLocationName = ""
        let endLocationName = ""

        // Extract start and end tower names from the Line level properties or summary
        if (project.data.Lines.properties) {
          if (project.data.Lines.properties["Start Location"]) {
            startTowerName = project.data.Lines.properties["Start Location"]
          }
          if (project.data.Lines.properties["End Location"]) {
            endTowerName = project.data.Lines.properties["End Location"]
          }
        }

        // Try to get from summary if not found in properties
        if ((!startTowerName || !endTowerName) && project.data.Lines.summary?.["Basic Information"]) {
          const basicInfo = project.data.Lines.summary["Basic Information"]

          if (!startTowerName && basicInfo["Start Location"]?.value) {
            startTowerName = basicInfo["Start Location"].value
          }

          if (!endTowerName && basicInfo["End Location"]?.value) {
            endTowerName = basicInfo["End Location"].value
          }

          // Try to find location names (these might be different from tower names)
          if (basicInfo["Start"]?.value) {
            startLocationName = basicInfo["Start"].value
          } else {
            startLocationName = startTowerName
          }

          if (basicInfo["End"]?.value) {
            endLocationName = basicInfo["End"].value
          } else {
            endLocationName = endTowerName
          }
        }

        console.log(`Project ${project.id} - Start Tower: ${startTowerName}, End Tower: ${endTowerName}`)

        // Store start/end tower info for this project
        if (startTowerName && endTowerName) {
          projectStartEndTowers[project.id] = {
            startTowerName,
            endTowerName,
            startLocationName,
            endLocationName,
          }
        }

        // Get voltage from project data
        let projectVoltage = 400 // Default to 400kV
        if (project.data.Lines.summary?.["Basic Information"]?.["Voltage"]?.value) {
          const voltageValue = project.data.Lines.summary["Basic Information"]["Voltage"].value
          projectVoltage = typeof voltageValue === "string" ? Number.parseInt(voltageValue, 10) : voltageValue
        }

        // Collect all towers
        const collectTowers = (node: any) => {
          if (node.type === "tower") {
            const lat = Number(node.properties?.latitude || node.properties?.Latitude || "0")
            const lng = Number(node.properties?.longitude || node.properties?.Longitude || "0")

            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              const towerKey = `${project.id}-${node.name}`

              allTowers[towerKey] = {
                id: towerKey,
                name: node.name,
                lat,
                lng,
                locationName: node.name, // Default to tower name
                projectId: project.id,
                projectName: project.name,
              }
            }
          }

          // Process children recursively
          if (node.children) {
            node.children.forEach(collectTowers)
          }
        }

        // Collect all line segments between adjacent towers
        const collectLineSegments = (node: any) => {
          if (node.type === "section" && node.children) {
            // Get all towers in this section
            const sectionTowers = node.children.filter((child: any) => child.type === "tower")

            // Sort towers by sequence if available
            sectionTowers.sort((a: any, b: any) => {
              const seqA = Number.parseInt(a.properties?.sequence || "0")
              const seqB = Number.parseInt(b.properties?.sequence || "0")
              return seqA - seqB
            })

            // Create line segments between adjacent towers
            for (let i = 0; i < sectionTowers.length - 1; i++) {
              const fromTower = sectionTowers[i]
              const toTower = sectionTowers[i + 1]

              const fromLat = Number(fromTower.properties?.latitude || fromTower.properties?.Latitude || "0")
              const fromLng = Number(fromTower.properties?.longitude || fromTower.properties?.Longitude || "0")
              const toLat = Number(toTower.properties?.latitude || toTower.properties?.Latitude || "0")
              const toLng = Number(toTower.properties?.longitude || toTower.properties?.Longitude || "0")

              if (
                !isNaN(fromLat) &&
                !isNaN(fromLng) &&
                !isNaN(toLat) &&
                !isNaN(toLng) &&
                fromLat !== 0 &&
                fromLng !== 0 &&
                toLat !== 0 &&
                toLng !== 0
              ) {
                // Get voltage from properties or summary, or use project voltage
                let voltage = projectVoltage

                if (node.properties?.voltage) {
                  voltage = Number(node.properties.voltage)
                } else if (node.summary?.["Circuit Details"]?.["Circuit 1"]?.["Voltage"]?.value) {
                  voltage = Number(node.summary["Circuit Details"]["Circuit 1"]["Voltage"].value)
                }

                allLines.push({
                  id: `${project.id}-${fromTower.name}-${toTower.name}`,
                  projectId: project.id,
                  projectName: project.name,
                  fromTower: fromTower.name,
                  toTower: toTower.name,
                  voltage,
                  path: [
                    { lat: fromLat, lng: fromLng },
                    { lat: toLat, lng: toLng },
                  ],
                })
              }
            }
          }

          // Process children recursively
          if (node.children) {
            node.children.forEach(collectLineSegments)
          }
        }

        // Execute both passes
        collectTowers(project.data.Lines)
        collectLineSegments(project.data.Lines)
      })

      // Now process the towers to identify start and end towers
      const processedTowersArray = Object.values(allTowers).map((tower) => {
        const [projectId, towerName] = tower.id.split("-", 2)
        const projectInfo = projectStartEndTowers[projectId]

        if (projectInfo) {
          // Check if this tower is a start or end tower
          if (towerName === projectInfo.startTowerName) {
            return {
              ...tower,
              isStartTower: true,
              isEndTower: false,
              locationName: projectInfo.startLocationName || towerName,
            }
          } else if (towerName === projectInfo.endTowerName) {
            return {
              ...tower,
              isStartTower: false,
              isEndTower: true,
              locationName: projectInfo.endLocationName || towerName,
            }
          }
        }

        // Not a start or end tower
        return {
          ...tower,
          isStartTower: false,
          isEndTower: false,
        }
      })

      setProcessedTowers(processedTowersArray)
      setProcessedLines(allLines)
      console.log(`Processed ${processedTowersArray.length} towers and ${allLines.length} line segments`)
    } catch (err) {
      console.error("Error processing project data:", err)
      setError(err instanceof Error ? err.message : "Failed to process project data")
    }
  }

  // Handle map load
  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map

      // If we have towers, fit the map to show all of them
      if (processedTowers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds()

        processedTowers.forEach((tower) => {
          bounds.extend(new window.google.maps.LatLng(tower.lat, tower.lng))
        })

        map.fitBounds(bounds, {
          padding: 50,
        })
      }
    },
    [processedTowers],
  )

  // Handle marker click
  const handleMarkerClick = useCallback((position: { lat: number; lng: number }, info: string) => {
    setSelectedMarker({ position, info })
  }, [])

  // Close info window
  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarker(null)
  }, [])

  // Toggle map labels
  const toggleLabels = useCallback(() => {
    if (mapRef.current) {
      // Store current center and zoom
      const currentCenter = mapRef.current.getCenter()
      const currentZoom = mapRef.current.getZoom()

      // Update state
      setShowLabels((prev) => !prev)

      // Apply the style change using the imported styles
      const styles = getMapStyles(!showLabels)
      mapRef.current.setOptions({ styles })

      // Restore center and zoom after a short delay to ensure styles are applied
      setTimeout(() => {
        if (mapRef.current && currentCenter) {
          mapRef.current.setCenter(currentCenter)
          if (currentZoom) mapRef.current.setZoom(currentZoom)
        }
      }, 10)
    } else {
      // Just update state if map isn't loaded yet
      setShowLabels((prev) => !prev)
    }
  }, [showLabels])

  // Add a useEffect to inject a style for the map labels
  useEffect(() => {
    if (isLoaded && mapRef.current) {
      // Add a style element to make map labels more visible
      const style = document.createElement("style")
      style.textContent = `
    .map-label {
      background-color: rgba(255, 255, 255, 0.7);
      padding: 2px 4px;
      border-radius: 2px;
      border: 1px solid #333;
      white-space: nowrap;
      font-size: 12px;
    }
  `
      document.head.appendChild(style)

      // Apply label visibility setting using the imported styles
      const styles = getMapStyles(!showLabels)
      mapRef.current.setOptions({ styles })

      return () => {
        document.head.removeChild(style)
      }
    }
  }, [isLoaded, showLabels])

  if (loadError) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Error loading Google Maps: {loadError.message}</Text>
      </View>
    )
  }

  if (!isLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading map...</Text>
      </View>
    )
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Processing project data...</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    )
  }

  if (processedTowers.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No tower data available in projects</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <GoogleMap
        mapContainerStyle={styles.mapContainer}
        center={{ lat: 0, lng: 0 }} // Will be overridden by fitBounds
        zoom={2} // Will be overridden by fitBounds
        options={{
          mapTypeControl: true,
          mapTypeControlOptions: {
            // Hide the default label toggle by restricting map types
            mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"],
            style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          },
          fullscreenControl: false,
          streetViewControl: true,
          zoomControl: true,
          styles: getMapStyles(!showLabels),
        }}
        onLoad={onMapLoad}
      >
        {/* Render polylines for all line segments */}
        {processedLines.map((line) => (
          <Polyline
            key={line.id}
            path={line.path}
            options={{
              strokeColor: getVoltageColor(line.voltage),
              strokeWeight: 3,
              strokeOpacity: 0.8,
              clickable: true,
            }}
            onClick={() => {
              // When clicking on a line, show info about the project
              const midpoint = {
                lat: (line.path[0].lat + line.path[1].lat) / 2,
                lng: (line.path[0].lng + line.path[1].lng) / 2,
              }
              handleMarkerClick(midpoint, `${line.projectName} - ${line.voltage}kV`)
            }}
          />
        ))}

        {/* Render markers for all towers */}
        {processedTowers.map((tower) => (
          <Marker
            key={tower.id}
            position={{ lat: tower.lat, lng: tower.lng }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 4,
              fillColor: tower.isStartTower || tower.isEndTower ? "#FF0000" : "#0000FF",
              fillOpacity: 0.8,
              strokeWeight: 1,
              strokeColor: "#FFFFFF",
            }}
            onClick={() =>
              handleMarkerClick(
                { lat: tower.lat, lng: tower.lng },
                `${tower.projectName}: ${tower.locationName || tower.name}${tower.isStartTower ? " (Start)" : tower.isEndTower ? " (End)" : ""
                }`,
              )
            }
          />
        ))}

        {/* Render text labels ONLY for start and end towers */}
        {showLabels &&
          processedTowers
            .filter((tower) => tower.isStartTower || tower.isEndTower)
            .map((tower) => (
              <Marker
                key={`label-${tower.id}`}
                position={{ lat: tower.lat, lng: tower.lng }}
                icon={{
                  path: "M 0,0 z", // Empty path = no visible marker
                  fillOpacity: 0,
                  strokeWeight: 0,
                  scale: 1,
                  labelOrigin: new window.google.maps.Point(0, 0),
                }}
                label={{
                  text: tower.locationName || tower.name,
                  color: "#000000",
                  fontWeight: "bold",
                  fontSize: "12px",
                  className: "map-label",
                }}
                onClick={() =>
                  handleMarkerClick(
                    { lat: tower.lat, lng: tower.lng },
                    `${tower.projectName}: ${tower.isStartTower ? "Start" : "End"}: ${tower.locationName || tower.name}`,
                  )
                }
              />
            ))}

        {/* Info window for selected marker */}
        {selectedMarker && (
          <InfoWindow position={selectedMarker.position} onCloseClick={handleInfoWindowClose}>
            <div style={{ padding: 4 }}>
              <p style={{ margin: 0, fontWeight: "bold" }}>{selectedMarker.info}</p>
            </div>
          </InfoWindow>
        )}

        {/* Add map controls */}
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            padding: "5px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "move",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "100%",
              height: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "4px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "3px",
                borderRadius: "1.5px",
                backgroundColor: theme.colors.outline,
                opacity: 0.5,
              }}
            />
          </div>
          <IconButton
            icon={() => <Tag size={20} color={showLabels ? theme.colors.primary : theme.colors.outline} />}
            onPress={toggleLabels}
            tooltip="Toggle Labels"
          />
        </div>
      </GoogleMap>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    overflow: "hidden",
  },
  mapContainer: {
    width: "100%",
    height: "100%",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 16,
  },
  errorText: {
    color: "red",
    textAlign: "center",
    padding: 16,
  },
})

